import { formatAddress } from "@inrev/common";
import type { SuretyAccount } from "@portal/api/account";
import { useFetchAccountLatestIndemnityAgreement } from "@portal/api/account";
import { bondFormFileUrlQueryKey } from "@portal/api/bond";
import {
	BondRequest,
	useCloseBondRequest,
	useFetchBondIssuance,
	useUploadAgentIssuedBondForm,
} from "@portal/api/bond-request/agent";
import { BondRequestProcessingBanner } from "@portal/components/features/banners";
import { FormItemLabel } from "@portal/components/form";
import { ConfirmationModalClickWrapper } from "@portal/components/layout/ConfirmationModalClickWrapper";
import { LoadingModal } from "@portal/components/layout/LoadingModal";
import { Modal } from "@portal/components/layout/Modal";
import { Banner, Button, Card, ClickableIcon, Icon, Text } from "@portal/components/primitives";
import { LocalFileUpload } from "@portal/components/ui/LocalFileUpload";
import { MoreActionsMenu } from "@portal/components/ui/MoreActionsMenu";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetFileUrl } from "../../../../../api";
import { bondRequestIssuanceMethodTypeLabelMap } from "../../../../../constants";
import { LocallyUploadedFile } from "../../../../../types";
import { ClosedReasonModal } from "../../../../shared/account/ClosedReasonModal";
import { AgentBondRequestHeaderNotice } from "../../../../shared/request/AgentBondRequestHeaderNotice";
import { BondRequestLayout } from "../../../../shared/request/BondRequestLayout";

const NOT_READY_FOR_AGENT_ISSUE_MESSAGE =
	"Not Ready for Agent Issue: Please wait until you receive a notification to issue the bond";

type PendingAgentIssueHeaderNoticeProps = {
	status: "not-ready" | "ready";
	bondNumber?: string;
};

export const PendingAgentIssueHeaderNotice = ({
	status,
	bondNumber,
}: PendingAgentIssueHeaderNoticeProps) => (
	<AgentBondRequestHeaderNotice
		icon={
			status === "ready" ? (
				<Icon type="play-circle" color="confirm" size="small" filled />
			) : (
				<Icon type="stop" color="danger" size="small" filled />
			)
		}
	>
		{status === "ready" ? (
			<>
				Ready for Agent Issue With Bond Number{" "}
				<Text className="font-mono font-normal ml-[5px] select-text">{bondNumber}</Text>
			</>
		) : (
			NOT_READY_FOR_AGENT_ISSUE_MESSAGE
		)}
	</AgentBondRequestHeaderNotice>
);

type PendingBondRequestViewProps = {
	request: BondRequest;
	account: SuretyAccount;
};

export const PendingBondRequestView = ({ request, account }: PendingBondRequestViewProps) => {
	const [agentIssuedBondFormFileUpload, setAgentIssuedBondFormFileUpload] = useState<
		LocallyUploadedFile<"filled_bond_form">[]
	>([]);
	const [showClosedReasonModal, setShowClosedReasonModal] = useState(false);
	const { closeBondRequest, closeBondRequestLoading, closeBondRequestError } = useCloseBondRequest(
		request.suretyAccountId,
		request.id,
	);
	const { uploadAgentIssuedBondForm, uploadAgentIssuedBondFormIsLoading } =
		useUploadAgentIssuedBondForm(request.suretyAccountId, request.id);
	const { getFileUrl: getBondFormFileUrl } = useGetFileUrl();
	const { bondIssuance, bondIssuanceIsLoading } = useFetchBondIssuance(
		request.suretyAccountId,
		request.id,
	);
	const { latestIndemnityAgreement, latestIndemnityAgreementIsLoading } =
		useFetchAccountLatestIndemnityAgreement(request.suretyAccountId);
	const agentIssueStatus = useMemo(() => {
		if (bondIssuance?.method === "agent") {
			if (bondIssuance.pendingBondFormReview || bondIssuance.pendingIndemnityAgreement)
				return "not-ready";
			if (bondIssuance.pendingAgentIssue) return "ready";
			if (bondIssuance.pendingAgentIssueAdminReview) return "complete";
		}
	}, [bondIssuance]);
	const agentIssueHeaderStatus =
		agentIssueStatus === "not-ready" || agentIssueStatus === "ready" ? agentIssueStatus : undefined;
	const [termsAndConditionsOpen, setTermsAndConditionsOpen] = useState<boolean>(false);

	if (bondIssuanceIsLoading || latestIndemnityAgreementIsLoading) return <LoadingModal />;

	return (
		<BondRequestLayout
			request={request}
			account={account}
			banner={
				<>
					<BondRequestProcessingBanner underwritingStatus={request.underwritingStatus} />
					{agentIssueStatus === "ready" && (
						<Card elevation="raised">
							<Card.Body className="flex flex-col gap-y-[15px] p-[30px]">
								<FormItemLabel required>Please upload the issued bond form</FormItemLabel>
								<LocalFileUpload
									value={agentIssuedBondFormFileUpload}
									onChange={setAgentIssuedBondFormFileUpload}
									allowDelete={true}
									allowedTypesAndLabels={{
										filled_bond_form: "Issued Bond Form",
									}}
									allowedExtensions={["pdf"]}
									maxFiles={1}
									analyticsContext="bond-request-attachments"
								/>
								<ConfirmationModalClickWrapper
									message="Are you sure?"
									confirmButtonText="Upload"
									confirmTrackingId="bond-request-agent-upload-issued-form-confirm"
									cancelTrackingId="bond-request-agent-upload-issued-form-cancel"
									onConfirm={() => uploadAgentIssuedBondForm(agentIssuedBondFormFileUpload[0].id)}
								>
									<Button
										color={agentIssuedBondFormFileUpload.length === 0 ? "secondary" : "primary"}
										variant="solid"
										className="w-fit mt-[3px] self-end"
										disabled={agentIssuedBondFormFileUpload.length === 0}
										trackingId="bond-request-agent-upload-issued-form"
									>
										Upload Issued Bond Form
									</Button>
								</ConfirmationModalClickWrapper>
							</Card.Body>
						</Card>
					)}
					{agentIssueStatus === "complete" && (
						<Banner tone="confirm">
							<Banner.Leading>
								<Icon type="check" color="confirm" size="small" />
							</Banner.Leading>
							<Banner.Content>Agent Issue Complete</Banner.Content>
						</Banner>
					)}
				</>
			}
			headerNotice={
				agentIssueHeaderStatus ? (
					<PendingAgentIssueHeaderNotice
						status={agentIssueHeaderStatus}
						bondNumber={bondIssuance?.bondNumber}
					/>
				) : undefined
			}
			actionButtons={
				<>
					<div className="w-full flex flex-col border border-gray-500 divide-y divide-gray-500 rounded text-[14px]">
						<div className="p-[5px] text-center text-[13px] italic">Bond Issuance Method</div>
						<div className="p-[8px] px-[12px] flex justify-center font-semibold">
							<div className="w-max">
								{bondIssuance?.method && bondRequestIssuanceMethodTypeLabelMap[bondIssuance.method]}
							</div>
						</div>
					</div>
					<MoreActionsMenu
						items={[
							...(() => {
								if (bondIssuance?.pendingAgentIssue || bondIssuance?.pendingAgentIssueAdminReview) {
									return [
										{
											label: "Download Bond Form",
											icon: <Icon type="arrow-down-tray" size="mini" />,
											onClick: () =>
												getBondFormFileUrl({
													baseUrl: `/v2/surety/accounts/${request.suretyAccountId}/quotes/${request.id}/bond-form/file`,
													queryKey: bondFormFileUrlQueryKey(request.id, true),
													asDownload: true,
												}),
											trackingId: "bond-request-agent-download-bond-form",
										},
									];
								}
								return [];
							})(),
							{
								label: "View Terms & Conditions",
								icon: <Icon type="eye" size="mini" />,
								onClick: () => setTermsAndConditionsOpen(true),
								trackingId: "bond-request-agent-view-terms",
							},
							{
								label: "Close Bond Request",
								icon: <Icon type="minus-circle" size="mini" />,
								onClick: () => setShowClosedReasonModal(true),
								trackingId: "bond-request-agent-close",
							},
						]}
						panelClassName="mt-[10px] w-[200px]"
					/>
				</>
			}
			termsAndConditionsOpen={termsAndConditionsOpen}
		>
			<PendingWaitCard
				agentIssueStatus={agentIssueStatus}
				bondIssuance={bondIssuance ?? undefined}
				indemnityStatus={latestIndemnityAgreement?.status}
				accountId={request.suretyAccountId}
				carrier={"carrier" in request ? request.carrier : undefined}
			/>
			{showClosedReasonModal && (
				<ClosedReasonModal
					closeFunction={closeBondRequest}
					closeFunctionError={closeBondRequestError}
					isClosing={closeBondRequestLoading}
					onDismiss={() => setShowClosedReasonModal(false)}
					closingAction="close"
				/>
			)}
			{(uploadAgentIssuedBondFormIsLoading || closeBondRequestLoading) && (
				<Modal>
					<LoadingModal />
				</Modal>
			)}
		</BondRequestLayout>
	);
};

type PendingWaitCardProps = {
	agentIssueStatus?: "not-ready" | "ready" | "complete";
	bondIssuance?: {
		pendingIndemnityAgreement?: boolean;
		pendingBondFormReview?: boolean;
		pendingInRevIssue?: boolean;
		pendingAgentIssue?: boolean;
		pendingAgentIssueAdminReview?: boolean;
		method?: string;
		bondNumber?: string;
	};
	indemnityStatus?: string;
	accountId: BondRequest["suretyAccountId"];
	carrier?: {
		legalName?: string;
		address?: Parameters<typeof formatAddress>[0];
	};
};

export const PendingWaitCard = ({
	agentIssueStatus,
	bondIssuance,
	indemnityStatus,
	accountId,
	carrier,
}: PendingWaitCardProps) => {
	const navigate = useNavigate();
	const showWaiting = agentIssueStatus !== "ready";
	const showHowToIssue = Boolean(bondIssuance?.pendingAgentIssue);

	if (!showWaiting && !showHowToIssue) return null;

	return (
		<Card
			elevation="raised"
			className="rounded-xl"
			data-testid="agent-bond-request-pending-wait-card"
		>
			<Card.Body className="p-6">
				<div className="flex flex-col w-full space-y-10">
					{showWaiting && (
						<div className="flex flex-col space-y-3.5">
							<Text type="title" color="primary">
								What are we waiting on?
							</Text>
							<ul className="space-y-2 px-[18px] list-disc">
								{bondIssuance?.pendingIndemnityAgreement && (
									<li>
										<div className="flex items-center">
											<Text type="body" color="primary">
												{indemnityStatus === "review"
													? "An underwriter to review the uploaded indemnity agreement"
													: "One or more indemnitors need to sign the indemnity agreement"}
											</Text>
											<ClickableIcon
												type="link"
												size="mini"
												color="secondary"
												aria-label="Open indemnity agreement"
												className="px-[5px]"
												onClick={() => navigate(`/accounts/${accountId}?st=indemnity`)}
											/>
										</div>
									</li>
								)}
								{bondIssuance?.pendingBondFormReview && (
									<li>
										<Text type="body" color="primary">
											An inRev admin is reviewing the uploaded bond form
										</Text>
									</li>
								)}
								{bondIssuance?.pendingInRevIssue && bondIssuance.method === "electronic" && (
									<li>
										<Text type="body" color="primary">
											An inRev admin is generating an electronic bond form with digital signatures
											and seals
										</Text>
									</li>
								)}
								{bondIssuance?.pendingInRevIssue && bondIssuance.method === "physical" && (
									<li>
										<Text type="body" color="primary">
											An inRev admin is generating a physical bond form with wet signatures and
											raised seals
										</Text>
									</li>
								)}
								{bondIssuance?.pendingAgentIssueAdminReview && (
									<li>
										<Text type="body" color="primary">
											An inRev admin is reviewing the agent issued bond form
										</Text>
									</li>
								)}
							</ul>
						</div>
					)}
					{showHowToIssue && (
						<div className="flex flex-col space-y-3.5">
							<Text type="title" color="primary">
								How do I issue this bond?
							</Text>
							<ol className="space-y-2 px-[18px] list-decimal">
								<li>
									<Text type="body" color="primary">
										Wait until you are notified that your bond is ready to issue
									</Text>
								</li>
								<li>
									<Text type="body" color="primary">
										Download the bond form
									</Text>
								</li>
								<li className="space-y-3 list-decimal list-item">
									<Text type="body" color="primary">
										Apply an effective date that is between:
									</Text>
									<ul className="list-disc list-inside bg-surface-neutral w-fit px-5 py-2.5 rounded-md">
										<li>
											<Text type="body" color="primary">
												2 days before the date of underwriting approval
											</Text>
										</li>
										<li>
											<Text type="body" color="primary">
												60 days after the date of underwriting approval
											</Text>
										</li>
									</ul>
								</li>
								{bondIssuance?.bondNumber ? (
									<li className="pt-[15px] pb-[10px]">
										<Text type="body" color="primary">
											Apply bond number{" "}
										</Text>
										<Text
											type="body"
											color="primary"
											className="font-mono bg-surface-neutral py-1.5 px-2.5 ml-[3px] rounded-sm"
										>
											{bondIssuance.bondNumber}
										</Text>
									</li>
								) : (
									<li>
										<Text type="body" color="primary">
											Apply the provided bond number
										</Text>
									</li>
								)}
								<li>
									<Text type="body" color="primary">
										Apply the{" "}
									</Text>
									<Text type="body-strong" color="primary" className="underline">
										{carrier?.legalName}
									</Text>
									<Text type="body" color="primary">
										{" "}
										seal{carrier?.address && " and address:"}
									</Text>
									{carrier?.address && (
										<Text
											type="body"
											color="primary"
											className="bg-surface-neutral py-1.5 px-2.5 ml-[3px] mt-1.5 rounded-md inline-block"
										>
											{formatAddress(carrier.address)}
										</Text>
									)}
								</li>
								<li>
									<Text type="body" color="primary">
										Upload the issued bond form
									</Text>
								</li>
							</ol>
						</div>
					)}
				</div>
			</Card.Body>
		</Card>
	);
};
